import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Library, BadgeCheck, RefreshCw, Trash2, Plus, ShieldAlert, ShieldCheck, AlertCircle, Clock,
} from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// CANDIDATE — Reference Library (cross-employer reuse)
// ---------------------------------------------------------------------------
export function ReferenceLibraryPanel() {
  const [items, setItems] = useState([]);
  const [apps, setApps] = useState([]);
  const [sharing, setSharing] = useState(null); // ref ids selected for share
  const [chosenApp, setChosenApp] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/references/library`);
      setItems(data.items || []);
      // Pull candidate's applications for the share target picker
      const a = await axios.get(`${API}/me/applications`).catch(() => ({ data: [] }));
      setApps(Array.isArray(a.data) ? a.data : []);
    } catch (err) { setError(err.response?.data?.detail || 'Could not load reference library'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSharing((s) => {
      const set = new Set(s || []);
      set.has(id) ? set.delete(id) : set.add(id);
      return [...set];
    });
  };

  const submitShare = async () => {
    if (!chosenApp || !sharing?.length) return;
    try {
      await axios.post(`${API}/me/references/share`, {
        application_id: chosenApp,
        reference_ids: sharing,
        note_to_employer: note,
      });
      window.alert(`${sharing.length} reference(s) attached to that application.`);
      setSharing(null); setChosenApp(''); setNote('');
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Share failed');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-5" data-testid="reference-library">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2"><Library className="w-4 h-4 text-blue-600" /> Reference Library</h3>
        <p className="text-xs text-gray-500">Reuse any verified reference for new applications — no re-asking the referee.</p>
      </div>

      {error && <p className="text-xs text-rose-600 mb-3 flex gap-1 items-center"><AlertCircle className="w-3 h-3" />{error}</p>}

      {loading ? (
        <div className="h-20 bg-gray-100 rounded animate-pulse" />
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">No references yet. Once a referee submits, you can reuse it here.</p>
      ) : (
        <>
          <ul className="divide-y border rounded-lg" data-testid="reference-list">
            {items.map((r) => {
              const selected = sharing?.includes(r.reference_id);
              return (
                <li key={r.reference_id} data-testid={`reference-item-${r.reference_id}`} className={`p-3 flex items-center gap-3 ${selected ? 'bg-blue-50' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!selected}
                    onChange={() => toggleSelect(r.reference_id)}
                    data-testid={`select-reference-${r.reference_id}`}
                    aria-label="Select reference to reuse"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{r.referee_name || 'Anonymous referee'}</p>
                      {r.verified && <BadgeCheck className="w-4 h-4 text-emerald-600 shrink-0" aria-label="Verified" />}
                      {r.credibility_score != null && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                          {r.credibility_score}% credibility
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {r.referee_title ? `${r.referee_title} · ` : ''}{r.referee_company || r.referee_email_domain || '—'}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 text-right shrink-0">
                    <p>Reused {r.reuse_count}×</p>
                    <p>{r.submitted_at?.slice(0, 10)}</p>
                  </div>
                </li>
              );
            })}
          </ul>

          {sharing?.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid="share-controls">
              <p className="text-sm font-medium">Reuse {sharing.length} reference{sharing.length === 1 ? '' : 's'} for:</p>
              <select
                value={chosenApp}
                onChange={(e) => setChosenApp(e.target.value)}
                data-testid="share-application-select"
                className="w-full px-2 py-1.5 border rounded text-sm"
              >
                <option value="">— select application —</option>
                {apps.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.role_title || a.requisition_id?.slice(0, 8)} · {a.current_stage}
                  </option>
                ))}
              </select>
              <textarea
                placeholder="Optional note to employer (e.g. 'Same referee, same role')"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 400))}
                rows={2}
                className="w-full px-2 py-1.5 border rounded text-xs"
              />
              <div className="flex gap-2">
                <button
                  onClick={submitShare}
                  disabled={!chosenApp}
                  data-testid="confirm-share-btn"
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-40"
                >
                  Attach to application
                </button>
                <button
                  onClick={() => { setSharing(null); setChosenApp(''); setNote(''); }}
                  className="px-3 py-1.5 text-gray-600 text-xs rounded"
                >Cancel</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CANDIDATE — Right-to-Work panel
// ---------------------------------------------------------------------------
const RTW_DOC_OPTIONS = [
  { v: 'uk_passport', l: 'UK passport' },
  { v: 'irish_passport', l: 'Irish passport' },
  { v: 'share_code', l: 'UK Share Code' },
  { v: 'biometric_residence_permit', l: 'Biometric Residence Permit' },
  { v: 'evisa', l: 'eVisa' },
  { v: 'passport_with_visa', l: 'Passport with visa' },
  { v: 'settlement_status', l: 'EU Settlement Status' },
  { v: 'us_passport', l: 'US passport' },
  { v: 'us_i9_list_a', l: 'US I-9 List A document' },
  { v: 'us_i9_list_b_c', l: 'US I-9 List B+C documents' },
  { v: 'us_eb_visa', l: 'US employment-based visa' },
  { v: 'eu_passport', l: 'EU passport' },
  { v: 'eu_id_card', l: 'EU national ID card' },
  { v: 'other', l: 'Other' },
];
const ELIGIBILITY = [
  { v: 'full', l: 'Full — no restrictions' },
  { v: 'restricted_hours', l: 'Restricted hours' },
  { v: 'restricted_role', l: 'Restricted role/sector' },
  { v: 'sponsorship_required', l: 'Sponsorship required' },
  { v: 'none', l: 'No right to work' },
];

export function RightToWorkPanel() {
  const [records, setRecords] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    document_type: 'uk_passport', country: 'GB', document_reference: '',
    issued_date: '', expiry_date: '', work_eligibility: 'full', restrictions_text: '',
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/right-to-work`);
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) { logger.error('rtw load', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/me/right-to-work`, {
        ...form,
        issued_date: form.issued_date || null,
        expiry_date: form.expiry_date || null,
        restrictions_text: form.restrictions_text || null,
      });
      setAdding(false);
      setForm({ document_type: 'uk_passport', country: 'GB', document_reference: '',
                issued_date: '', expiry_date: '', work_eligibility: 'full', restrictions_text: '' });
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to record');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this Right-to-Work record?')) return;
    try {
      await axios.delete(`${API}/me/right-to-work/${id}`);
      load();
    } catch (err) { window.alert(err.response?.data?.detail || 'Failed'); }
  };

  const statusBadge = (status, days) => {
    if (status === 'expired') return { c: 'bg-rose-100 text-rose-700', t: 'Expired' };
    if (status === 'expiring_soon') return { c: 'bg-amber-100 text-amber-700', t: `Expires in ${days}d` };
    if (status === 'approaching') return { c: 'bg-yellow-100 text-yellow-700', t: `Expires in ${days}d` };
    if (status === 'valid') return { c: 'bg-emerald-100 text-emerald-700', t: 'Valid' };
    return { c: 'bg-slate-100 text-slate-600', t: 'No expiry' };
  };

  return (
    <div className="bg-white rounded-lg shadow p-5" data-testid="rtw-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Right to Work</h3>
        {!adding && (
          <button onClick={() => setAdding(true)} data-testid="add-rtw-btn" className="text-xs px-2 py-1 bg-emerald-600 text-white rounded">+ Add document</button>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Banking compliance requires verified RTW. We store a hash + last-4 only — never the full document number.
      </p>

      {adding && (
        <form onSubmit={submit} className="space-y-2 mb-4 p-3 bg-gray-50 rounded text-sm" data-testid="rtw-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Document type</label>
              <select required value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} data-testid="rtw-doc-type" className="w-full px-2 py-1 border rounded text-sm">
                {RTW_DOC_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Country (ISO 2-letter)</label>
              <input required maxLength={2} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} className="w-full px-2 py-1 border rounded text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Document number / share code</label>
            <input required minLength={4} value={form.document_reference} onChange={(e) => setForm({ ...form, document_reference: e.target.value })} data-testid="rtw-doc-ref" className="w-full px-2 py-1 border rounded text-sm font-mono" />
            <p className="text-xs text-gray-400 mt-1">Stored as one-way hash + last 4 only.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Issued</label>
              <input type="date" value={form.issued_date} onChange={(e) => setForm({ ...form, issued_date: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Expiry (required for visas)</label>
              <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="rtw-expiry" className="w-full px-2 py-1 border rounded text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Work eligibility</label>
            <select required value={form.work_eligibility} onChange={(e) => setForm({ ...form, work_eligibility: e.target.value })} data-testid="rtw-eligibility" className="w-full px-2 py-1 border rounded text-sm">
              {ELIGIBILITY.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          {form.work_eligibility !== 'full' && (
            <textarea placeholder="Restrictions (e.g. 20h/wk during studies)" rows={2} value={form.restrictions_text} onChange={(e) => setForm({ ...form, restrictions_text: e.target.value.slice(0, 400) })} className="w-full px-2 py-1 border rounded text-xs" />
          )}
          <div className="flex gap-2">
            <button type="submit" data-testid="save-rtw-btn" className="px-3 py-1 bg-emerald-600 text-white rounded text-xs">Save</button>
            <button type="button" onClick={() => setAdding(false)} className="px-3 py-1 text-gray-600 rounded text-xs">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="h-20 bg-gray-100 rounded animate-pulse" />
      ) : records.length === 0 && !adding ? (
        <p className="text-sm text-gray-500 py-4">No documents on file.</p>
      ) : (
        <ul className="space-y-2" data-testid="rtw-list">
          {records.map((r) => {
            const badge = statusBadge(r.expiry_status, r.days_to_expiry);
            return (
              <li key={r.id} data-testid={`rtw-item-${r.id}`} className="p-3 border rounded">
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{RTW_DOC_OPTIONS.find((o) => o.v === r.document_type)?.l || r.document_type}</p>
                    <p className="text-xs text-gray-500">
                      {r.country} · ends •••{r.document_reference_last4}{r.expiry_date ? ` · exp ${r.expiry_date}` : ''}
                    </p>
                    {r.restrictions_text && <p className="text-xs text-amber-700 mt-1">⚠ {r.restrictions_text}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${badge.c}`} data-testid={`rtw-status-${r.id}`}>{badge.t}</span>
                    {r.status === 'verified' && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 flex items-center gap-0.5">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    )}
                    <button onClick={() => remove(r.id)} aria-label="Remove" data-testid={`rtw-delete-${r.id}`} className="text-gray-400 hover:text-rose-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
